import { FastForward } from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';
import { useGame } from 'contexts/Game/Game';
import { useSettings } from 'contexts/Settings/Settings';
import { getRandomTruckName } from 'helpers';
import { useEffect, useMemo, useState } from 'react';
import Image from '../Image/Image';
import { TruckNameInput } from './Truck.styles';

const animationDurations: TruckAnimationDurations = {
    'intro': 2,
    'open-1': 12,
    'open-2': 12,
    'open-3': 12,
    'open-4': 12,
    'open-5': 14,
    'outro': 8,
};

const nameOffsets: TruckNameOffsets = {
    'open-3': [100, 0],
    'open-4': [-80, 0],
    'open-5': [-80, 0],
    'outro': [-140, 65],
};

const showNameDelays: TruckShowNameDelays = {
    'open-1': [2],
    'open-2': [2],
    'open-3': [2.5],
    'open-4': [2],
    'open-5': [2.5],
    'outro': [2, 2.5],
};

export default function Truck({ animation = 'still', editable, onAnimationComplete }: TruckProps) {

    const [showName, setShowName] = useState(false);
    const game = useGame();
    const settings = useSettings();

    const nameOffset = useMemo(() => {
        return nameOffsets[animation] || [0, 0];
    }, [
        animation,
    ]);

    const showNameDelay = useMemo(() => {
        return showNameDelays[animation] || (animation !== 'still' && [2]) || null;
    }, [
        animation,
    ]);

    useEffect(() => {
        const animationDuration = animationDurations[animation];
        if (animationDuration && !settings.debugChart && !!onAnimationComplete) {
            const timer = setTimeout(onAnimationComplete, animationDuration * 1000);

            return () => {
                clearTimeout(timer);
            };
        }
    }, [
        animation,
        settings.debugChart,
        onAnimationComplete,
    ]);

    useEffect(() => {
        if (showNameDelay) {
            // Add timeouts for name
            const [show, hide] = showNameDelay;
            setTimeout(() => setShowName(true), show * 1000);

            if (hide) {
                setTimeout(() => setShowName(false), (hide + show) * 1000);
            }

        } else {
            setShowName(true);
        }
    }, [
        showNameDelay,
    ]);

    function handleBlur(e: any) {
        const { value } = e.target;
        const name = (value && value.length >= 2) ? value : getRandomTruckName();
        handleNameChange(name);
    }

    function handleChange(e: any) {
        handleNameChange(e.target.value);
    }

    function handleNameChange(value: string) {
        game.setTruckName(value);
    }

    return (
        <Box
            sx={{
                alignItems: 'center',
                display: 'flex',
                height: 360,
                justifyContent: 'center',
                marginBottom: -3,
                position: 'relative'
            }}>
            <Box
                sx={{
                    height: 360,
                    left: 'calc(50% - 320px)',
                    position: 'absolute',
                    top: 0,
                    width: 640
                }}>
                <Image alt="Truck" src={`/assets/truck/truck-${animation}.gif`} />
            </Box>
            {showName && (
                <Box
                    sx={{
                        alignItems: 'center',
                        display: 'flex',
                        height: 32,
                        justifyContent: 'center',
                        left: `calc(50% - ${150 - nameOffset[0]}px)`,
                        position: 'absolute',
                        top: 90 + nameOffset[1],
                        width: 230,
                        zIndex: 1
                    }}>
                    {(editable || showNameDelay) ? (
                        <TruckNameInput
                            disabled={!editable}
                            value={game.truckName}
                            onBlur={handleBlur}
                            onChange={handleChange}
                        />
                    ) : (
                        <Typography
                            sx={{
                                color: '#141414',
                                fontSize: 18,
                                lineHeight: 1
                            }}>
                            {game.truckName}
                        </Typography>
                    )}
                </Box>
            )}
            {!!onAnimationComplete && (
                <Box
                    sx={{
                        position: 'absolute',
                        right: 0,
                        bottom: '10%'
                    }}>
                    <Button
                        color="inherit"
                        startIcon={
                            <FastForward fontSize="inherit" />
                        }
                        size="small"
                        variant="text"
                        onClick={onAnimationComplete}
                    >
                        SKIP
                    </Button>
                </Box>
            )}
        </Box>
    );

}
